/**
 * Notification routing helpers
 * Only returns a route when both app and feature are valid.
 * If invalid, returns null so caller can avoid navigation.
 */
import { appFeatures } from '@/types/routing';

type RouteBuilder = (feature: string) => string;

// Central map of supported notification target apps → route builders
// NOTE: Keep in sync with appFeatures and high-level router config.
const routeMap: Record<string, RouteBuilder> = {
  profile: (feature) => `/profile?feature=${feature}`,
  kyc: (feature) => `/kyc?feature=${feature}`,
  compliance: (feature) => `/compliance?feature=${feature}`,
  ipo: (feature) => `/ipo?feature=${feature}`,
  mtf: (feature) => `/mtf?feature=${feature}`,
  accounts: (feature) => `/accounts?feature=${feature}`,
  reports: (feature) => `/reports?feature=${feature}`,
  wealth: (feature) => `/wealth?feature=${feature}`,
};

/**
 * Validate app + feature combination and build route.
 * @returns string route or null if invalid
 */
export const getNotificationRoute = (app?: string, feature?: string): string | null => {
  if (!app || !feature) return null;
  // Ensure app exists in feature map
  if (!(app in appFeatures)) return null;
  // Validate feature within that app
  const featureKeys = Object.keys(appFeatures[app as keyof typeof appFeatures]);
  if (!featureKeys.includes(feature)) return null;
  // Ensure we have a route builder
  const builder = routeMap[app];
  if (!builder) return null;
  return builder(feature);
};

// Backward compatibility export (if any legacy usages remain)
export const routes = (app: string, feature: string): string | null =>
  getNotificationRoute(app, feature);
